/**
 * Document management service
 * Handles all API calls for document operations
 */

import { apiClient } from '../utils/api-client';
import {
  EntityType,
  type Document,
  type DocumentCollection,
  type CreateCollectionRequest,
  type UpdateCollectionRequest,
  type UploadDocumentRequest,
  type UploadEntityDocumentRequest,
  type DocumentUploadResponse,
  type DocumentSearchRequest,
  type DocumentSearchResponse,
  type CollectionContentsResponse,
  type DocumentFilters,
  type FundDocumentContextType,
  type InvestorDocumentFoldersResponse,
} from '../types/document';

/**
 * Triggers a browser download from a blob
 * @param blob - The blob to download
 * @param filename - The name for the downloaded file
 */
export function downloadBlobAsFile(blob: Blob, filename: string): void {
  try {
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();

    // Cleanup
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error('Download failed:', error);
  }
}

export async function downloadUrlAsFile(
  url: string,
  filename: string
): Promise<void> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch: ${response.statusText}`);
  }
  downloadBlobAsFile(await response.blob(), filename);
}

export const documentService = {
  // Collection operations
  async getCollections(params?: {
    parent_id?: string;
    entity_type?: string;
    entity_id?: string;
    limit?: number;
    offset?: number;
  }): Promise<DocumentCollection[]> {
    return apiClient.get<DocumentCollection[]>('/documents/collections', {
      params,
    });
  },

  async getCollection(id: string): Promise<DocumentCollection> {
    return apiClient.get<DocumentCollection>(`/documents/collections/${id}`);
  },

  async createCollection(
    data: CreateCollectionRequest
  ): Promise<DocumentCollection> {
    return apiClient.post<DocumentCollection>('/documents/collections', data);
  },

  async updateCollection(
    id: string,
    data: UpdateCollectionRequest
  ): Promise<DocumentCollection> {
    return apiClient.put<DocumentCollection>(
      `/documents/collections/${id}`,
      data
    );
  },

  async deleteCollection(id: string): Promise<void> {
    return apiClient.delete(`/documents/collections/${id}`);
  },

  async getCollectionContents(id: string): Promise<CollectionContentsResponse> {
    const response = await apiClient.get<CollectionContentsResponse>(
      `/documents/collections/${id}/contents`
    );
    return response;
  },

  // Document operations
  async getDocument(id: string): Promise<Document> {
    return apiClient.get<Document>(`/documents/${id}`);
  },

  async uploadDocument(
    data: UploadDocumentRequest
  ): Promise<DocumentUploadResponse> {
    const formData = new FormData();
    // Backend expects 'file' field for single file upload
    formData.append('file', data.file);

    // collection_id is required by backend
    if (!data.collection_id) {
      throw new Error('collection_id is required for document upload');
    }
    formData.append('collection_id', data.collection_id);

    // Add filename explicitly to ensure it's preserved
    formData.append('filename', data.file.name);

    if (data.metadata) {
      formData.append('metadata', JSON.stringify(data.metadata));
    }

    if (data.tags) {
      formData.append('tags', JSON.stringify(data.tags));
    }

    if (data.entity_associations) {
      formData.append(
        'entity_associations',
        JSON.stringify(data.entity_associations)
      );
    }

    if (data.document_source) {
      formData.append('document_source', data.document_source);
    }

    if (data.document_types && data.document_types.length > 0) {
      formData.append('document_types', JSON.stringify(data.document_types));
    }

    const response = await apiClient.post<DocumentUploadResponse>(
      '/documents/upload',
      formData
    );
    return response;
  },

  // Upload a single document scoped to a specific entity (fund, investor,
  // portfolio company). The who-tag is derived server-side from the
  // entityType/entityId in the URL, so the client never sends an
  // entity_association for the owning entity.
  async uploadEntityDocument(
    entityType: string,
    entityId: string,
    data: UploadEntityDocumentRequest
  ): Promise<DocumentUploadResponse> {
    const formData = new FormData();
    formData.append('file', data.file);
    formData.append('collection_id', data.collection_id);
    formData.append('filename', data.filename ?? data.file.name);

    if (data.metadata) {
      formData.append('metadata', JSON.stringify(data.metadata));
    }

    if (data.tags) {
      formData.append('tags', JSON.stringify(data.tags));
    }

    if (data.document_source) {
      formData.append('document_source', data.document_source);
    }

    if (data.document_types && data.document_types.length > 0) {
      formData.append('document_types', JSON.stringify(data.document_types));
    }

    if (data.portal_visible !== undefined) {
      formData.append('portal_visible', String(data.portal_visible));
    }

    if (data.fund_id) {
      formData.append('fund_id', data.fund_id);
    }

    if (data.context_type) {
      formData.append('context_type', data.context_type);
    }

    const response = await apiClient.post<DocumentUploadResponse>(
      `/documents/entities/${entityType}/${entityId}/documents/upload`,
      formData
    );
    return response;
  },

  async uploadMultipleDocuments(data: {
    files: File[];
    collection_id?: string;
    metadata?: Record<string, unknown>;
    tags?: string[];
    entity_associations?: {
      entity_type: string;
      entity_id: string;
      role: string;
      context_type?: string;
      context_name?: string;
    }[];
  }): Promise<DocumentUploadResponse[]> {
    const formData = new FormData();

    // Append all files
    data.files.forEach((file) => {
      formData.append('files', file);
    });

    // Append filenames to ensure they're preserved
    data.files.forEach((file) => {
      formData.append('filenames', file.name);
    });

    if (data.collection_id) {
      formData.append('collection_id', data.collection_id);
    }

    if (data.metadata) {
      formData.append('metadata', JSON.stringify(data.metadata));
    }

    if (data.tags) {
      formData.append('tags', JSON.stringify(data.tags));
    }

    if (data.entity_associations) {
      formData.append(
        'entity_associations',
        JSON.stringify(data.entity_associations)
      );
    }

    const response = await apiClient.post<DocumentUploadResponse[]>(
      '/documents/upload/bulk',
      formData
    );
    return response;
  },

  async getDocumentBlob(id: string): Promise<Blob> {
    // Use download-url endpoint for CDN-friendly access
    const downloadUrlResponse = await this.getDownloadUrl(id);

    if (!downloadUrlResponse?.download_url) {
      throw new Error('Download URL not available');
    }

    // Fetch the presigned URL as blob
    const response = await fetch(downloadUrlResponse.download_url);
    if (!response.ok) {
      throw new Error(`Failed to fetch document: ${response.statusText}`);
    }

    return await response.blob();
  },

  async downloadDocument(id: string, documentName: string): Promise<void> {
    try {
      // Always use download-url endpoint (works for all storage types)
      const downloadUrlResponse = await this.getDownloadUrl(id);

      if (downloadUrlResponse?.download_url) {
        await downloadUrlAsFile(downloadUrlResponse.download_url, documentName);
      } else {
        throw new Error('Download URL not available');
      }
    } catch (error) {
      console.error('Download failed:', error);
      throw error;
    }
  },

  async getDownloadUrl(
    id: string
  ): Promise<{ download_url: string; expires_in: number }> {
    return apiClient.get<{ download_url: string; expires_in: number }>(
      `/documents/${id}/download-url`
    );
  },

  async deleteDocument(id: string): Promise<void> {
    return apiClient.delete(`/documents/${id}`);
  },

  // Search operations
  async searchDocuments(
    request: DocumentSearchRequest
  ): Promise<DocumentSearchResponse> {
    return apiClient.post<DocumentSearchResponse>('/documents/search', request);
  },

  // Entity-specific operations
  async getEntityDocuments(
    entityType: string,
    entityId: string
  ): Promise<Document[]> {
    return apiClient.get<Document[]>(
      `/documents/entities/${entityType}/${entityId}/documents`
    );
  },

  async getEntityCollections(
    entityType: string,
    entityId: string
  ): Promise<DocumentCollection[]> {
    return apiClient.get<DocumentCollection[]>(
      `/documents/entities/${entityType}/${entityId}/collections`
    );
  },

  async getEntityCollectionContents(
    entityType: string,
    entityId: string,
    collectionId: string
  ): Promise<CollectionContentsResponse> {
    return apiClient.get<CollectionContentsResponse>(
      `/documents/entities/${entityType}/${entityId}/collections/${collectionId}/contents`
    );
  },

  // Fund-specific operations (using generic entity endpoints)
  async getFundDocuments(fundId: string): Promise<Document[]> {
    return this.getEntityDocuments(EntityType.FUND, fundId);
  },

  async getFundCollections(fundId: string): Promise<DocumentCollection[]> {
    return this.getEntityCollections(EntityType.FUND, fundId);
  },

  async createFundCollection(
    fundId: string,
    data: CreateCollectionRequest
  ): Promise<DocumentCollection> {
    return this.createEntityCollection(EntityType.FUND, fundId, data);
  },

  // Investor-specific operations (using generic entity endpoints)
  async getInvestorDocuments(investorId: string): Promise<Document[]> {
    return this.getEntityDocuments(EntityType.INVESTOR, investorId);
  },

  async getInvestorCollections(
    investorId: string
  ): Promise<DocumentCollection[]> {
    return this.getEntityCollections(EntityType.INVESTOR, investorId);
  },

  async createInvestorCollection(
    investorId: string,
    data: CreateCollectionRequest
  ): Promise<DocumentCollection> {
    return this.createEntityCollection(EntityType.INVESTOR, investorId, data);
  },

  // Portfolio-specific operations (using generic entity endpoints)
  async getPortfolioDocuments(portfolioId: string): Promise<Document[]> {
    return this.getEntityDocuments(EntityType.PORTFOLIO_COMPANY, portfolioId);
  },

  async getPortfolioCollections(
    portfolioId: string
  ): Promise<DocumentCollection[]> {
    return this.getEntityCollections(EntityType.PORTFOLIO_COMPANY, portfolioId);
  },

  async createPortfolioCollection(
    portfolioId: string,
    data: CreateCollectionRequest
  ): Promise<DocumentCollection> {
    return this.createEntityCollection(
      EntityType.PORTFOLIO_COMPANY,
      portfolioId,
      data
    );
  },

  // Generic entity collection creation
  async createEntityCollection(
    entityType: EntityType,
    entityId: string,
    data: CreateCollectionRequest
  ): Promise<DocumentCollection> {
    const response = await apiClient.post<{ data: DocumentCollection }>(
      `/documents/entities/${entityType}/${entityId}/collections`,
      data
    );
    return response.data;
  },

  // Get or create entity folder using the backend's system folder service
  async getEntityFolder(
    entityType: string,
    entityId: string
  ): Promise<{ folder_id: string }> {
    return apiClient.get<{ folder_id: string }>(
      `/documents/entities/${entityType}/${entityId}/folder`
    );
  },

  // Utility functions
  async buildDocumentTree(
    collections: DocumentCollection[]
  ): Promise<DocumentCollection[]> {
    const collectionMap = new Map<string, DocumentCollection>();
    const rootCollections: DocumentCollection[] = [];

    // First pass: create map and initialize children arrays
    collections.forEach((collection) => {
      collectionMap.set(collection.id, { ...collection, children: [] });
    });

    // Second pass: build tree structure
    collections.forEach((collection) => {
      const collectionWithChildren = collectionMap.get(collection.id)!;

      if (collection.parent_collection_id) {
        const parent = collectionMap.get(collection.parent_collection_id);
        if (parent) {
          parent.children!.push(collectionWithChildren);
        }
      } else {
        rootCollections.push(collectionWithChildren);
      }
    });

    return rootCollections;
  },

  async getDocumentBreadcrumbs(
    collectionId: string
  ): Promise<{ id: string; name: string; type: 'folder' | 'root' }[]> {
    const breadcrumbs: { id: string; name: string; type: 'folder' | 'root' }[] =
      [];

    let currentCollection = await this.getCollection(collectionId);

    while (currentCollection) {
      breadcrumbs.unshift({
        id: currentCollection.id,
        name: currentCollection.name,
        type: 'folder',
      });

      if (currentCollection.parent_collection_id) {
        currentCollection = await this.getCollection(
          currentCollection.parent_collection_id
        );
      } else {
        break;
      }
    }

    // Add root
    breadcrumbs.unshift({
      id: 'root',
      name: 'Documents',
      type: 'root',
    });

    return breadcrumbs;
  },

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return (
      Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    );
  },

  getFileIcon(contentType: string): string {
    // Map content types to icon names (assumes lucide-react icons)
    const typeMap: Record<string, string> = {
      'application/pdf': 'FileText',
      'application/msword': 'FileText',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        'FileText',
      'application/vnd.ms-excel': 'FileSpreadsheet',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
        'FileSpreadsheet',
      'application/vnd.ms-powerpoint': 'Presentation',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation':
        'Presentation',
      'text/plain': 'FileText',
      'text/csv': 'FileSpreadsheet',
      'image/jpeg': 'Image',
      'image/png': 'Image',
      'image/svg+xml': 'Image',
      'application/zip': 'Archive',
      'application/x-zip-compressed': 'Archive',
    };

    return typeMap[contentType] || 'File';
  },

  // Investor visibility operations
  async updateDocumentInvestorVisibility(
    documentId: string,
    entityType: string,
    entityId: string,
    investorVisible: boolean
  ): Promise<void> {
    return apiClient.post(
      `/documents/entities/${entityType}/${entityId}/documents/${documentId}/visibility`,
      { investor_visible: investorVisible }
    );
  },

  async updateDocumentFundScope(
    documentId: string,
    entityType: string,
    entityId: string,
    scopeFundId: string | null,
    contextType: FundDocumentContextType | null
  ): Promise<void> {
    return apiClient.post(
      `/documents/entities/${entityType}/${entityId}/documents/${documentId}/fund-scope`,
      { scope_fund_id: scopeFundId, context_type: contextType }
    );
  },

  async getInvestorDocumentFolders(
    investorId: string
  ): Promise<InvestorDocumentFoldersResponse> {
    return apiClient.get<InvestorDocumentFoldersResponse>(
      `/documents/entities/investors/${investorId}/fund-folders`
    );
  },

  async bulkUpdateInvestorVisibility(
    entityType: string,
    entityId: string,
    documentIds: string[],
    investorVisible: boolean
  ): Promise<{ updated_count: number }> {
    return apiClient.post(
      `/documents/entities/${entityType}/${entityId}/documents/visibility/bulk`,
      { document_ids: documentIds, investor_visible: investorVisible }
    );
  },

  buildSearchQuery(filters: DocumentFilters): DocumentSearchRequest {
    const request: DocumentSearchRequest = {};

    if (filters.searchTerm) {
      request.query = filters.searchTerm;
    }

    if (filters.entity_type) {
      request.entity_type = filters.entity_type;
    }

    if (filters.entity_id) {
      request.entity_id = filters.entity_id;
    }

    if (filters.content_type) {
      request.content_type = filters.content_type;
    }

    if (filters.tags && filters.tags.length > 0) {
      request.tags = filters.tags;
    }

    if (filters.date_from) {
      request.date_from = filters.date_from;
    }

    if (filters.date_to) {
      request.date_to = filters.date_to;
    }

    return request;
  },
};
